import { useEffect, useRef, type RefObject } from "react";
import { DefaultBrowserBehavior } from "amazon-chime-sdk-js";
import { usePlatformStore } from "@platform/store";
import { VIDEO_HEALTH_CHECK_INTERVAL_MS } from "@core/features/video/video.constants";
import { logMeetingEvent } from "@core/features/videoMeeting/utils/logMeetingEvent";

/**
 * Detects when a <video> element has no decodable frames while the tile
 * state says 'video'. After 2 consecutive failed checks, force-transitions
 * the attendee to 'frozen' so the canvas fallback activates.
 *
 * Also monitors tiles it froze: when the video element recovers (valid
 * readyState and dimensions), transitions back to 'video'. Only recovers
 * tiles frozen by this hook — bandwidth-paused, source-loss, and
 * reconnect-guard frozen tiles are untouched.
 *
 * Detection has two layers:
 * 1. Dead element: readyState < 2 or videoWidth/videoHeight === 0
 *    (Chrome clears these when the track becomes undecodable)
 * 2. Stale buffer: readyState and dimensions look valid, but
 *    getVideoPlaybackQuality().totalVideoFrames stops advancing
 *    (Chrome holds the last decoded frame in the buffer)
 *
 * Layer 2 addresses the documented blind spot where Chrome holds valid
 * readyState/videoWidth on stale decoded frames.
 */
export function useVideoHealthCheck(
  videoRef: RefObject<HTMLVideoElement | null>,
  attendeeId: string,
  tileState: string,
) {
  const failCountRef = useRef(0);
  const frozenByHealthCheckRef = useRef(false);
  const freezeStartRef = useRef<number | null>(null);
  const lastFrameCountRef = useRef<number | null>(null);
  const lastCurrentTimeRef = useRef<number | null>(null);
  const staleCountRef = useRef(0);
  const setAttendeeTileState = usePlatformStore((s) => s.setAttendeeTileState);

  useEffect(() => {
    // DefaultBrowserBehavior is constructed inline (mirrors useDeviceHotPlug
    // and AudioOutputControl). Per-effect-run cost is one allocation per tile
    // mount, not per tick — negligible.
    const isChromiumWebRTC = new DefaultBrowserBehavior().hasChromiumWebRTC();

    // Reset tracking when tile leaves frozen for other reasons
    if (tileState !== 'frozen') {
      frozenByHealthCheckRef.current = false;
    }

    // Monitor both 'video' (detect dead) and 'frozen' (detect recovery)
    if (tileState !== 'video' && tileState !== 'frozen') {
      failCountRef.current = 0;
      lastFrameCountRef.current = null;
      lastCurrentTimeRef.current = null;
      staleCountRef.current = 0;
      return;
    }

    const id = setInterval(() => {
      const el = videoRef.current;
      if (!el) return;

      const isDead =
        el.readyState < 2 || el.videoWidth === 0 || el.videoHeight === 0;

      // Stale-buffer detection. Two browser-specific paths with different
      // thresholds:
      //
      // Chromium: getVideoPlaybackQuality().totalVideoFrames stops advancing.
      // 2 consecutive stuck ticks (~6s). Tuned for Chrome's decoder.
      // Safari/Firefox stall longer on simulcast layer transitions, which
      // motivated the original Chromium-only gate (4949c121: meeting 5937
      // produced ~1,800 false positives on Safari/Firefox observers in a
      // 30-min session at this threshold).
      //
      // Non-Chromium: <video>.currentTime stops advancing. 4 consecutive
      // stuck ticks (~12s). The longer threshold absorbs the Safari/Firefox
      // simulcast-transition stall band that motivated the original gate.
      // currentTime advancement and totalVideoFrames advancement are both
      // functions of decoded-frame cadence, so the same root signal that
      // drove the Chromium false-positive class would drive a non-Chromium
      // false-positive class at 6s. 12s = real freeze territory. Costs ~6s
      // of additional detection latency on real freezes; the production
      // case that motivated this branch (session 11 of meeting 5936, chat-
      // reported iOS Safari freeze that persisted ~12+ minutes) is well
      // outside this latency tolerance. See
      // backend/skills/or/video-qa/artifacts/video-meeting-5936-session-11-followup-2026-05-04.md §1.1.
      let isStale = false;
      let currentTimeStall = false;
      if (!isDead && tileState === 'video') {
        if (isChromiumWebRTC) {
          const quality = (el as HTMLVideoElement & { getVideoPlaybackQuality?: () => { totalVideoFrames: number } }).getVideoPlaybackQuality?.();
          if (quality) {
            const currentFrames = quality.totalVideoFrames;
            if (lastFrameCountRef.current !== null && currentFrames === lastFrameCountRef.current) {
              staleCountRef.current++;
              if (staleCountRef.current >= 2) {
                isStale = true;
              }
            } else {
              staleCountRef.current = 0;
            }
            lastFrameCountRef.current = currentFrames;
          }
        } else {
          const currentTime = el.currentTime;
          if (lastCurrentTimeRef.current !== null && currentTime === lastCurrentTimeRef.current) {
            staleCountRef.current++;
            // 4-tick threshold: see comment block above for why this is
            // double the Chromium threshold.
            if (staleCountRef.current >= 4) {
              isStale = true;
              currentTimeStall = true;
            }
          } else {
            staleCountRef.current = 0;
          }
          lastCurrentTimeRef.current = currentTime;
        }
      }

      if (isDead || isStale) {
        if (tileState === 'video') {
          // For isDead, require 2 consecutive failures (existing behavior).
          // For isStale, the staleCountRef already accumulated 2 checks.
          if (isStale || ++failCountRef.current >= 2) {
            setAttendeeTileState(attendeeId, 'frozen');
            frozenByHealthCheckRef.current = true;
            freezeStartRef.current = Date.now();
            failCountRef.current = 0;
            staleCountRef.current = 0;
            lastFrameCountRef.current = null;
            lastCurrentTimeRef.current = null;

            // Log freeze event (fire-and-forget)
            const store = usePlatformStore.getState();
            logMeetingEvent({
              event_type: 'health_check_freeze',
              meeting_id: store.meetingId,
              room_id: store.roomId,
              user: store.user ? { id: store.user.id } : null,
              attendee_id: attendeeId,
              ready_state: el.readyState,
              video_width: el.videoWidth,
              video_height: el.videoHeight,
              stale_buffer: isStale && !currentTimeStall,
              currenttime_stall: currentTimeStall,
            });
          }
        }
      } else {
        failCountRef.current = 0;
        // staleCountRef is owned by the inner stale-detection block above —
        // it's reset there when frames actually advance (the inner else
        // branch). Resetting it here would clobber accumulated stale-tick
        // count between consecutive stuck-frame ticks, since each healthy-
        // looking-but-stuck tick takes this branch (isDead && isStale are
        // both false until the counter reaches the threshold).
        // Recovery: only recover tiles frozen by THIS hook
        if (tileState === 'frozen' && frozenByHealthCheckRef.current) {
          setAttendeeTileState(attendeeId, 'video');

          // Log recovery event with freeze duration
          const freezeDuration = freezeStartRef.current
            ? Math.round((Date.now() - freezeStartRef.current) / 1000)
            : null;
          const store = usePlatformStore.getState();
          logMeetingEvent({
            event_type: 'health_check_recovery',
            meeting_id: store.meetingId,
            room_id: store.roomId,
            user: store.user ? { id: store.user.id } : null,
            attendee_id: attendeeId,
            freeze_duration_seconds: freezeDuration,
          });

          frozenByHealthCheckRef.current = false;
          freezeStartRef.current = null;
          lastFrameCountRef.current = null;
          lastCurrentTimeRef.current = null;
        }
      }
    }, VIDEO_HEALTH_CHECK_INTERVAL_MS);

    return () => {
      clearInterval(id);
      failCountRef.current = 0;
      staleCountRef.current = 0;
      lastFrameCountRef.current = null;
      lastCurrentTimeRef.current = null;
    };
  }, [tileState, attendeeId, videoRef, setAttendeeTileState]);
}
